import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLeaveImpersonationMutation } from '@/features/auth/api/mutations'
import { useImpersonationStatusQuery } from '@/features/auth/api/impersonationQueries'
import { Button } from '@/shared/components/ui/button'
import { isSafeExternalUrl } from '@/shared/lib/safeRedirect'

function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

export function ImpersonationBanner() {
  const { t } = useTranslation()
  const status = useImpersonationStatusQuery(true)
  const leave = useLeaveImpersonationMutation()
  const [nowMs, setNowMs] = useState(() => Date.now())

  const active = status.data?.active === true
  const startedAt = status.data?.started_at

  useEffect(() => {
    if (!active || startedAt == null) {
      return
    }
    const id = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [active, startedAt])

  useEffect(() => {
    if (status.data?.active === false && status.data.reason != null && status.data.reason !== 'none') {
      const url = status.data.admin_url
      if (isSafeExternalUrl(url)) {
        window.location.assign(url)
      }
    }
  }, [status.data])

  if (!active || startedAt == null) {
    return null
  }

  const elapsed = Math.max(0, Math.floor((nowMs - Date.parse(startedAt)) / 1000))

  const onLeave = () => {
    leave.mutate(undefined, {
      onSuccess: (data) => {
        if (isSafeExternalUrl(data.admin_url)) {
          window.location.assign(data.admin_url)
        }
      },
    })
  }

  return (
    <div
      role="status"
      className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 border-b border-amber-700/40 bg-amber-500 px-4 py-2 text-sm text-amber-950"
    >
      <p className="font-medium">
        {t('auth.impersonate.banner_active', { elapsed: formatElapsed(elapsed) })}
      </p>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="bg-amber-950 text-amber-50 hover:bg-amber-900"
        disabled={leave.isPending}
        onClick={onLeave}
      >
        {t('auth.impersonate.banner_leave')}
      </Button>
    </div>
  )
}
